import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Award, TrendingUp, DollarSign, Wrench, Mail, MessageSquare, Phone, Download, X, ExternalLink, ChevronLeft, ChevronRight, Info, Maximize2, Minimize2, AlertTriangle, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Plain-English definitions (shown as tooltips — one meaning per number).
const DEFS = {
  billed: 'All customer and warranty/HSP work billed for the selected dates. Customer Billed + Warranty/HSP Billed (no invoice counted twice).',
  collected: 'Money actually received.',
  owed: 'Billed money not yet received. Total Billed / Sold − Total Collected.',
  jobs: 'Jobs with billable customer or warranty work.',
  avg_job: 'Average Job = Total Billed / Sold ÷ Revenue Jobs.',
  collection: 'Overall Collection % = Total Collected ÷ Total Billed / Sold.',
  cust_collection: 'Customer-Side Collection % = Customer Collected ÷ Customer Billed. This is different from Overall Collection %.',
};

// Small, tap/hover tooltip for definitions (mobile-friendly).
const InfoTip = ({ text, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex align-middle ml-1">
      <button
        type="button"
        data-testid={testid}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="text-[#737373] hover:text-[#D4AF37]"
        aria-label="Definition"
      >
        <Info className="w-3 h-3" />
      </button>
      {open && (
        <span className="absolute z-50 left-1/2 -translate-x-1/2 top-5 w-56 rounded-md border border-[#3f3f3f] bg-[#0A0A0A] p-2 text-[11px] leading-snug text-[#D4D4D4] shadow-xl normal-case font-normal tracking-normal">
          {text}
        </span>
      )}
    </span>
  );
};

// Plain-language job status → color + label (color is never the only signal).
const JOB_STATUS_STYLE = (s) => {
  const k = (s || '').toLowerCase();
  if (k.includes('paid in full')) return 'text-green-300 border-green-500/40 bg-green-500/10';
  if (k.includes('entered'))      return 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10';
  if (k.includes('ready for'))    return 'text-cyan-300 border-cyan-500/40 bg-cyan-500/10';
  if (k.includes('hsp'))          return 'text-purple-300 border-purple-500/40 bg-purple-500/10';
  if (k.includes('customer'))     return 'text-amber-300 border-amber-500/40 bg-amber-500/10';
  if (k.includes('review'))       return 'text-orange-300 border-orange-500/40 bg-orange-500/10';
  if (k.includes('no charge'))    return 'text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]';
  if (k.includes('estimate'))     return 'text-blue-300 border-blue-500/40 bg-blue-500/10';
  return 'text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]';
};

// ────────────────────────────────────────────────────────────
// 1) TECH PERFORMANCE — iter127: unified accounting + day filter + drill-down
// ────────────────────────────────────────────────────────────
// iter248 — TZ-safe date-string math (parses at UTC noon so no local off-by-one).
const addDaysIso = (iso, n) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
// iter202 — local-date formatting + Sat→Fri week / month bounds (matches Schedule).
const fmtLocal = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
const weekBoundsOf = (anchorIso) => {
  const d = new Date(anchorIso + 'T00:00:00');
  const back = (d.getDay() + 1) % 7;                 // Saturday = start of payroll week
  const start = new Date(d); start.setDate(d.getDate() - back);
  const end = new Date(start); end.setDate(start.getDate() + 6);
  return { start: fmtLocal(start), end: fmtLocal(end) };
};
const monthBoundsOf = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return { start: fmtLocal(new Date(y, m - 1, 1)), end: fmtLocal(new Date(y, m, 0)) };
};
const shiftWeek = (anchorIso, deltaWeeks) => {
  const d = new Date(anchorIso + 'T00:00:00'); d.setDate(d.getDate() + deltaWeeks * 7); return fmtLocal(d);
};
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const MONTH_LABEL = (ym) => { const [y, m] = ym.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }); };

export const TechPerformance = () => {
  // Window mode: 'preset' = last-N-days dropdown; 'day' = single day; 'range' = custom range.
  const [mode, setMode] = useState('preset');
  const [days, setDays] = useState(30);
  // iter248 — Anchors start EMPTY and are seeded from the server's America/New_York
  // "today" (server_today_et) once the first response arrives — never the device clock.
  const [serverToday, setServerToday] = useState(null);
  const [singleDay, setSingleDay] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [weekAnchor, setWeekAnchor] = useState('');
  const [monthAnchor, setMonthAnchor] = useState('');
  const [rows, setRows] = useState([]);
  const [windowInfo, setWindowInfo] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('revenue_collected');
  const [detailTechId, setDetailTechId] = useState(null);
  const [showOffice, setShowOffice] = useState(false);   // QB / office-processing columns

  // Resolve the actual {start,end} based on mode → one shared truth for the table + drill-down.
  const resolvedRange = (() => {
    if (mode === 'day')   return singleDay ? { start: singleDay, end: singleDay } : { start: '', end: '' };
    if (mode === 'range') return { start: startDate, end: endDate };
    if (mode === 'week')  return weekAnchor ? weekBoundsOf(weekAnchor) : { start: '', end: '' };
    if (mode === 'month') return monthAnchor ? monthBoundsOf(monthAnchor) : { start: '', end: '' };
    return { start: '', end: '' };
  })();

  const queryString = (() => {
    const r = resolvedRange;
    if (r.start && r.end) return `start=${r.start}&end=${r.end}`;
    return `days=${days}`;
  })();

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/dashboards/tech-performance?${queryString}`, { withCredentials: true });
    setRows(data.rows || []);
    setWindowInfo({ start: data.start || '', end: data.end || '' });
    if (data.server_today_et) setServerToday(data.server_today_et);
  }, [queryString]);
  useEffect(() => { load(); }, [load]);

  // iter248 — Seed all period anchors from the server's ET "today" the first time
  // it is known. Device clock is never the source of truth for reporting periods.
  useEffect(() => {
    if (!serverToday) return;
    setSingleDay((v) => v || serverToday);
    setStartDate((v) => v || addDaysIso(serverToday, -30));
    setEndDate((v) => v || serverToday);
    setWeekAnchor((v) => v || serverToday);
    setMonthAnchor((v) => v || serverToday.slice(0, 7));
  }, [serverToday]);

  const sorted = [...rows].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));

  return (
    <div data-testid="dashboard-tech-performance" className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1">Technician Performance</h2>
          <p className="text-[#A3A3A3] text-sm">
            How much each tech sold, how much we collected, and who still owes us.
            {windowInfo.start && <span className="text-[#525252] ml-2">{windowInfo.start} → {windowInfo.end}</span>}
          </p>
          <p className="text-[#525252] text-[11px] mt-1">
            Hover the <Info className="inline w-3 h-3" /> icons for a plain-English definition of each number. QuickBooks / office-processing detail lives under <b className="text-[#A3A3A3]">Office Details</b> (it is a status, not extra money).
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-[130px] bg-[#0A0A0A] border-[#262626] text-white" data-testid="tech-perf-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626]">
              <SelectItem value="preset" className="text-white">Last N days</SelectItem>
              <SelectItem value="week"   className="text-white">Week</SelectItem>
              <SelectItem value="month"  className="text-white">Month</SelectItem>
              <SelectItem value="day"    className="text-white">Single day</SelectItem>
              <SelectItem value="range"  className="text-white">Custom range</SelectItem>
            </SelectContent>
          </Select>
          {mode === 'preset' && (
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-[140px] bg-[#0A0A0A] border-[#262626] text-white" data-testid="tech-perf-window">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626]">
                {[1, 7, 14, 30, 60, 90].map((d) => (
                  <SelectItem key={d} value={String(d)} className="text-white">
                    Last {d} {d === 1 ? 'day' : 'days'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {mode === 'week' && (
            <div className="flex items-center gap-1" data-testid="tech-perf-week-nav">
              <Button variant="outline" size="icon" onClick={() => setWeekAnchor((a) => shiftWeek(a, -1))} data-testid="tech-perf-week-prev" className="h-9 w-9 border-[#262626] text-white hover:bg-[#262626]"><ChevronLeft className="w-4 h-4" /></Button>
              <div className="text-center px-2 min-w-[150px]">
                <div className="text-xs text-white font-medium" data-testid="tech-perf-week-label">{weekAnchor ? `${weekBoundsOf(weekAnchor).start} → ${weekBoundsOf(weekAnchor).end}` : '…'}</div>
                <button onClick={() => serverToday && setWeekAnchor(serverToday)} className="text-[10px] text-[#D4AF37] hover:underline">This week</button>
              </div>
              <Button variant="outline" size="icon" onClick={() => setWeekAnchor((a) => shiftWeek(a, 1))} data-testid="tech-perf-week-next" className="h-9 w-9 border-[#262626] text-white hover:bg-[#262626]"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
          {mode === 'month' && (
            <div className="flex items-center gap-1" data-testid="tech-perf-month-nav">
              <Button variant="outline" size="icon" onClick={() => setMonthAnchor((a) => shiftMonth(a, -1))} data-testid="tech-perf-month-prev" className="h-9 w-9 border-[#262626] text-white hover:bg-[#262626]"><ChevronLeft className="w-4 h-4" /></Button>
              <div className="text-center px-2 min-w-[150px]">
                <div className="text-xs text-white font-medium" data-testid="tech-perf-month-label">{monthAnchor ? MONTH_LABEL(monthAnchor) : '…'}</div>
                <button onClick={() => serverToday && setMonthAnchor(serverToday.slice(0, 7))} className="text-[10px] text-[#D4AF37] hover:underline">This month</button>
              </div>
              <Button variant="outline" size="icon" onClick={() => setMonthAnchor((a) => shiftMonth(a, 1))} data-testid="tech-perf-month-next" className="h-9 w-9 border-[#262626] text-white hover:bg-[#262626]"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
          {mode === 'day' && (
            <Input
              data-testid="tech-perf-single-day"
              type="date"
              value={singleDay}
              onChange={(e) => setSingleDay(e.target.value)}
              className="bg-[#0A0A0A] border-[#262626] text-white w-[160px]"
            />
          )}
          {mode === 'range' && (
            <>
              <Input data-testid="tech-perf-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-[150px]" />
              <span className="text-[#525252]">→</span>
              <Input data-testid="tech-perf-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="bg-[#0A0A0A] border-[#262626] text-white w-[150px]" />
            </>
          )}
        </div>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          data-testid="tech-perf-office-toggle"
          onClick={() => setShowOffice((v) => !v)}
          className="text-xs inline-flex items-center gap-1.5 text-[#A3A3A3] hover:text-white border border-[#262626] rounded px-2.5 h-8"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showOffice ? 'rotate-180' : ''}`} />
          {showOffice ? 'Hide' : 'Show'} Office Details (QuickBooks)
        </button>
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3] uppercase text-[10px] tracking-wider">
            <tr>
              {[
                ['technician_name', 'Technician', null],
                ['revenue_billed', 'Total Billed / Sold', DEFS.billed],
                ['revenue_collected', 'Total Collected', DEFS.collected],
                ['outstanding_total', 'Still Owed', DEFS.owed],
                ['revenue_jobs', 'Revenue Jobs', DEFS.jobs],
                ['avg_job', 'Average Job', DEFS.avg_job],
                ['collection_rate', 'Collection %', DEFS.collection],
              ].map(([k, label, def]) => (
                <th key={k} onClick={() => setSortBy(k)} className={`px-3 py-2 text-left cursor-pointer hover:text-white whitespace-nowrap ${sortBy === k ? 'text-[#D4AF37]' : ''}`} data-testid={`tech-col-${k}`}>
                  {label}{def && <InfoTip text={def} testid={`tech-col-info-${k}`} />}
                </th>
              ))}
              {showOffice && (
                <>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Customer Billed</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Warranty/HSP Billed</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Pending QuickBooks</th>
                  <th className="px-3 py-2 text-left whitespace-nowrap">Entered in QuickBooks</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={showOffice ? 11 : 7} className="px-3 py-8 text-center text-[#A3A3A3]">No tech activity in this window</td></tr>
            ) : sorted.map((r) => (
              <tr key={r.technician_id} data-testid={`tech-row-${r.technician_id}`} className={`border-b border-[#262626] hover:bg-[#0A0A0A] ${r.is_unassigned ? 'bg-amber-500/5' : ''}`}>
                <td className="px-3 py-2 text-white font-medium">
                  <button
                    type="button"
                    data-testid={`tech-drill-${r.technician_id}`}
                    onClick={() => setDetailTechId(r.technician_id)}
                    className={`text-left hover:underline inline-flex items-center gap-1 ${r.is_unassigned ? 'text-amber-300 hover:text-amber-200' : 'text-white hover:text-[#D4AF37]'}`}
                  >
                    {r.is_unassigned && <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 mr-1">Fix</span>}
                    {r.technician_name}
                    {r.is_unassigned && r.invoices_total ? <span className="text-[10px] text-amber-400/70 ml-1">({r.invoices_total})</span> : null}
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </button>
                </td>
                <td className="px-3 py-2 text-white font-bold">{fmtMoney(r.revenue_billed)}</td>
                <td className="px-3 py-2 text-[#D4AF37] font-bold">{fmtMoney(r.revenue_collected)}</td>
                <td className="px-3 py-2 text-red-300 font-medium">{fmtMoney(r.outstanding_total)}</td>
                <td className="px-3 py-2 text-white">{r.revenue_jobs ?? 0}</td>
                <td className="px-3 py-2 text-white">{fmtMoney(r.avg_job)}</td>
                <td className="px-3 py-2"><span className={r.collection_rate >= 80 ? 'text-green-400' : r.collection_rate >= 50 ? 'text-[#D4AF37]' : 'text-red-400'}>{r.collection_rate}%</span></td>
                {showOffice && (
                  <>
                    <td className="px-3 py-2 text-white">{fmtMoney(r.customer_billed)}</td>
                    <td className="px-3 py-2 text-purple-300">{fmtMoney(r.warranty_billed)}</td>
                    <td className="px-3 py-2 text-orange-300">{fmtMoney(r.pending_office_qb_total)}{r.pending_office_qb_count ? <span className="text-[#525252] text-[10px] ml-1">({r.pending_office_qb_count})</span> : null}</td>
                    <td className="px-3 py-2 text-green-400">{fmtMoney(r.entered_in_qb_total)}{r.entered_in_qb_count ? <span className="text-[#525252] text-[10px] ml-1">({r.entered_in_qb_count})</span> : null}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showOffice && (
        <p className="text-[11px] text-[#737373]">
          <b className="text-[#A3A3A3]">Office Details</b> show QuickBooks processing status — a subset of already-billed work. These are <b>not</b> additional revenue and are never added to Total Billed / Sold again.
        </p>
      )}

      {/* iter127 — Drill-down sheet for a single tech. */}
      <TechDetailSheet
        techId={detailTechId}
        queryString={queryString}
        onClose={() => setDetailTechId(null)}
        onAssigned={load}
        techName={(sorted.find((r) => r.technician_id === detailTechId) || {}).technician_name}
      />
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// TechDetailSheet — iter127 drill-down per tech.
// ────────────────────────────────────────────────────────────
const STATUS_BADGE = (s) => {
  const k = (s || '').toLowerCase();
  if (k === 'paid')          return 'text-green-300 border-green-500/40 bg-green-500/10';
  if (k === 'deposit_paid')  return 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10';
  if (k === 'draft')         return 'text-[#A3A3A3] border-[#3f3f3f] bg-[#262626]';
  if (k === 'refunded' || k === 'partially_refunded') return 'text-red-300 border-red-500/40 bg-red-500/10';
  return 'text-amber-300 border-amber-500/40 bg-amber-500/10';
};

// ────────────────────────────────────────────────────────────
// JobCard — one card per service job (customer + HSP grouped together).
// ────────────────────────────────────────────────────────────
const JobCard = ({ job, expanded, onToggle, isUnassigned, onAssign }) => {
  const allInv = [...(job.customer_invoices || []), ...(job.warranty_invoices || [])];
  return (
    <div data-testid={`job-card-${job.job_key}`} className={`rounded-md border ${job.needs_review ? 'border-orange-500/50 bg-orange-500/5' : 'border-[#262626] bg-[#0A0A0A]'}`}>
      <button type="button" onClick={onToggle} data-testid={`job-card-toggle-${job.job_key}`} className="w-full text-left p-3 flex items-start gap-3">
        <ChevronDown className={`w-4 h-4 mt-1 text-[#737373] shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-white font-medium">{job.end_customer || <span className="text-orange-300">Unknown customer</span>}</span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${JOB_STATUS_STYLE(job.status)}`}>{job.status}</span>
            {job.needs_review && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border border-orange-500/50 bg-orange-500/10 text-orange-300 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Needs Review
              </span>
            )}
          </div>
          <p className="text-[#A3A3A3] text-xs mt-0.5">
            {job.service_date || '—'} · {job.work_performed || 'Service job'}
            {job.warranty_invoices?.length > 0 && job.customer_invoices?.length > 0 && <span className="text-purple-300"> · Customer + HSP</span>}
          </p>
          {job.needs_review && (
            <ul className="mt-1.5 space-y-0.5">
              {job.flags.map((f) => (
                <li key={f} className="text-[11px] text-orange-300">Needs Review — {f.toLowerCase()}.</li>
              ))}
            </ul>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-white font-bold">{fmtMoney(job.total_billed)}</p>
          <p className="text-[11px] text-[#A3A3A3]">Collected {fmtMoney(job.total_collected)}</p>
          {job.still_owed > 0 && <p className="text-[11px] text-red-300">Owed {fmtMoney(job.still_owed)}</p>}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#262626] px-3 py-2 space-y-2">
          {allInv.length === 0 && <p className="text-[#737373] text-xs">No invoice records.</p>}
          {allInv.map((r) => (
            <div key={r.invoice_id} data-testid={`job-inv-${r.invoice_id}`} className="flex flex-wrap items-center justify-between gap-2 text-xs border border-[#1f1f1f] rounded px-2 py-1.5">
              <div className="min-w-0">
                <span className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded mr-1.5 ${r.is_warranty ? 'bg-purple-500/15 text-purple-300' : 'bg-[#262626] text-[#A3A3A3]'}`}>{r.is_warranty ? 'HSP / Warranty' : 'Customer'}</span>
                {r.invoice_id
                  ? <Link to={`/admin/invoices?id=${r.invoice_id}`} className="text-white hover:text-[#D4AF37] hover:underline">{r.invoice_number || 'Invoice'}</Link>
                  : <span className="text-white">{r.invoice_number || 'Invoice'}</span>}
                {r.is_warranty && (
                  <span className="block text-[10px] text-[#A3A3A3] mt-0.5">
                    <span className="text-[#737373]">Payor:</span> {r.payor_name || 'Warranty'} · <span className="text-[#737373]">For customer:</span> {r.for_customer || <span className="text-orange-300">missing</span>}
                  </span>
                )}
                <span className={`ml-1 text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded border ${STATUS_BADGE(r.status)}`}>{r.status || '—'}</span>
                {r.excluded_reason && <span className="ml-1 text-[10px] text-red-400 italic">excluded: {r.excluded_reason}</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-white">Billed {fmtMoney(r.billed_amount)}</span>
                <span className="text-[#D4AF37] font-bold">Collected {fmtMoney(r.collected_amount)}</span>
                {isUnassigned && (
                  <button type="button" data-testid={`assign-tech-open-${r.invoice_id}`} onClick={() => onAssign(r)} className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20">Assign</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// TechDetailSheet — grouped job & invoice detail (full-screen capable).
// ────────────────────────────────────────────────────────────
const TechDetailSheet = ({ techId, queryString, techName, onClose, onAssigned }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [assignInv, setAssignInv] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [fullBreakdown, setFullBreakdown] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState({});
  const [search, setSearch] = useState('');
  const scrollRef = useRef(null);
  const savedScroll = useRef(0);
  const isUnassigned = techId === '__unassigned__';

  useEffect(() => {
    if (!techId) { setData(null); return; }
    setLoading(true);
    axios.get(`${API}/admin/dashboards/tech-performance/${techId}/details?${queryString}`, { withCredentials: true })
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load tech details'))
      .finally(() => setLoading(false));
  }, [techId, queryString, reloadKey]);

  // Preserve scroll position across the drawer ↔ full-screen toggle.
  useLayoutEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = savedScroll.current;
  }, [fullscreen]);
  const toggleFullscreen = () => {
    if (scrollRef.current) savedScroll.current = scrollRef.current.scrollTop;
    setFullscreen((f) => !f);
  };

  const handleAssigned = () => {
    setAssignInv(null);
    setReloadKey((k) => k + 1);
    if (onAssigned) onAssigned();
  };

  if (!techId) return null;
  const a = data?.accounting || {};
  const recon = data?.reconciliation;
  const jobs = (data?.jobs || []).filter((j) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (j.end_customer || '').toLowerCase().includes(q)
      || (j.work_performed || '').toLowerCase().includes(q)
      || (j.status || '').toLowerCase().includes(q);
  });

  const bigCards = [
    { l: 'Total Billed / Sold', v: a.billed_total,     cls: 'text-white',    def: DEFS.billed, key: 'billed' },
    { l: 'Total Collected',     v: a.collected_total,  cls: 'text-[#D4AF37]', def: DEFS.collected, key: 'collected' },
    { l: 'Still Owed',          v: a.outstanding_total, cls: 'text-red-300',  def: DEFS.owed, key: 'owed' },
    { l: 'Revenue Jobs',        v: a.revenue_jobs,     cls: 'text-white', raw: true, def: DEFS.jobs, key: 'jobs' },
  ];

  return (
    <div className="fixed inset-0 z-50" data-testid="tech-detail-overlay">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={scrollRef}
        data-testid="tech-detail-sheet"
        className={`absolute top-0 right-0 h-full bg-[#141414] border-l border-[#262626] text-white overflow-y-auto ${fullscreen ? 'left-0 w-full border-l-0' : 'w-full sm:max-w-4xl'}`}
      >
        <div className="sticky top-0 z-10 bg-[#141414] border-b border-[#262626] p-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-white font-['Outfit'] text-lg font-bold">{isUnassigned ? 'Unassigned / Not Credited' : (techName || 'Technician')} — Job & Invoice Detail</h3>
            <p className="text-[#A3A3A3] text-xs">{data?.start} → {data?.end}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button type="button" data-testid="tech-detail-fullscreen" onClick={toggleFullscreen} title={fullscreen ? 'Return to drawer' : 'Expand to full screen'} className="text-[#A3A3A3] hover:text-white p-1.5 rounded hover:bg-[#262626]">
              {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button type="button" data-testid="tech-detail-close" onClick={onClose} title="Close" className="text-[#A3A3A3] hover:text-white p-1.5 rounded hover:bg-[#262626]">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading && <p className="text-[#A3A3A3] text-sm p-4">Loading…</p>}
        {data && (
          <div className={`p-4 space-y-5 ${fullscreen ? 'max-w-5xl mx-auto' : ''}`}>
            {/* Reconciliation warning (spec §9) */}
            {recon && (!recon.billed_matches || !recon.collected_matches) && (
              <div data-testid="tech-detail-recon-warning" className="rounded-md border border-orange-500/50 bg-orange-500/10 p-3 text-sm text-orange-200 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Needs Review — the job rows below don’t add up to this tech’s totals.
                  {!recon.billed_matches && <> Billed differs by <b>{fmtMoney(Math.abs(recon.billed_diff))}</b>.</>}
                  {!recon.collected_matches && <> Collected differs by <b>{fmtMoney(Math.abs(recon.collected_diff))}</b>.</>}
                </span>
              </div>
            )}
            {data.needs_review_count > 0 && (
              <div data-testid="tech-detail-needs-review-count" className="text-xs text-orange-300 inline-flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {data.needs_review_count} job{data.needs_review_count === 1 ? '' : 's'} flagged for office review below.
              </div>
            )}

            {/* Four big cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {bigCards.map((s) => (
                <div key={s.l} className="rounded-md border border-[#262626] bg-[#0A0A0A] p-4">
                  <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider flex items-center">{s.l}<InfoTip text={s.def} testid={`tile-info-${s.key}`} /></p>
                  <p className={`text-2xl font-bold ${s.cls} mt-1`} data-testid={`tech-detail-tile-${s.key}`}>{s.raw ? (s.v ?? 0) : fmtMoney(s.v)}</p>
                </div>
              ))}
            </div>

            {/* Simple breakdown */}
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3">
                <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mb-2">Billed</p>
                <div className="flex justify-between text-sm"><span className="text-[#A3A3A3]">Customer billed</span><span className="text-white">{fmtMoney(a.billed_customer)}</span></div>
                <div className="flex justify-between text-sm mt-1"><span className="text-[#A3A3A3]">Warranty/HSP billed</span><span className="text-purple-300">{fmtMoney(a.billed_warranty)}</span></div>
              </div>
              <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3">
                <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mb-2">Collected</p>
                <div className="flex justify-between text-sm"><span className="text-[#A3A3A3]">Customer collected</span><span className="text-white">{fmtMoney(a.collected_customer)}</span></div>
                <div className="flex justify-between text-sm mt-1"><span className="text-[#A3A3A3]">Warranty/HSP collected</span><span className="text-purple-300">{fmtMoney(a.collected_warranty)}</span></div>
                <div className="flex justify-between text-xs mt-2 pt-2 border-t border-[#262626]"><span className="text-[#737373]">Customer-Side Collection<InfoTip text={DEFS.cust_collection} testid="tile-info-cust-coll" /></span><span className="text-white">{a.customer_collection_rate}%</span></div>
                <div className="flex justify-between text-xs mt-1"><span className="text-[#737373]">Overall Collection<InfoTip text={DEFS.collection} testid="tile-info-overall-coll" /></span><span className="text-white">{a.collection_rate}%</span></div>
              </div>
              <div className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3">
                <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mb-2">Office Processing <span className="text-[#525252] normal-case">(subset of billed)</span></p>
                <div className="flex justify-between text-sm"><span className="text-[#A3A3A3]">Needs review</span><span className="text-orange-300">{fmtMoney(a.office_review_pending_total)}</span></div>
                <div className="flex justify-between text-sm mt-1"><span className="text-[#A3A3A3]">Pending QuickBooks</span><span className="text-orange-300">{fmtMoney(a.pending_office_qb_total)}</span></div>
                <div className="flex justify-between text-sm mt-1"><span className="text-[#A3A3A3]">Entered in QuickBooks</span><span className="text-green-400">{fmtMoney(a.entered_in_qb_total)}</span></div>
              </div>
            </div>

            {/* Job cards */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">Jobs ({jobs.length})</p>
                <Input data-testid="tech-detail-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer, work, status…" className="bg-[#0A0A0A] border-[#262626] text-white h-8 w-56 text-xs" />
              </div>
              {jobs.length === 0 ? (
                <p className="text-[#737373] text-sm py-6 text-center">No jobs in this window.</p>
              ) : jobs.map((j) => (
                <JobCard
                  key={j.job_key}
                  job={j}
                  expanded={!!expandedJobs[j.job_key]}
                  onToggle={() => setExpandedJobs((p) => ({ ...p, [j.job_key]: !p[j.job_key] }))}
                  isUnassigned={isUnassigned}
                  onAssign={setAssignInv}
                />
              ))}
            </div>

            {/* Advanced — collapsed by default */}
            <div>
              <button type="button" data-testid="tech-detail-full-breakdown" onClick={() => setFullBreakdown((v) => !v)} className="text-xs inline-flex items-center gap-1.5 text-[#A3A3A3] hover:text-white border border-[#262626] rounded px-2.5 h-8">
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${fullBreakdown ? 'rotate-180' : ''}`} />
                {fullBreakdown ? 'Hide' : 'Show'} Full Breakdown
              </button>
              {fullBreakdown && (
                <div className="mt-3 space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { l: 'Average Job', v: a.avg_job },
                      { l: 'Drafts (excluded)', v: a.draft_total },
                      { l: 'Refunded (excluded)', v: a.refunded_total },
                      { l: 'Invoices', v: a.billed_count, raw: true },
                    ].map((s) => (
                      <div key={s.l} className="rounded-md border border-[#262626] bg-[#0A0A0A] p-3">
                        <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">{s.l}</p>
                        <p className="text-lg font-bold text-white mt-1">{s.raw ? (s.v ?? 0) : fmtMoney(s.v)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3] uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="px-3 py-2 text-left">Perf. Date</th>
                          <th className="px-3 py-2 text-left">Customer</th>
                          <th className="px-3 py-2 text-left">Invoice #</th>
                          <th className="px-3 py-2 text-right">Billed</th>
                          <th className="px-3 py-2 text-right">Collected</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.invoices || []).map((r) => (
                          <tr key={r.invoice_id} data-testid={`tech-detail-row-${r.invoice_id}`} className="border-b border-[#262626]">
                            <td className="px-3 py-2 text-white whitespace-nowrap">{r.performance_date || r.date}</td>
                            <td className="px-3 py-2 text-white">{r.customer_name || '—'}</td>
                            <td className="px-3 py-2 text-white">{r.invoice_id ? <Link to={`/admin/invoices?id=${r.invoice_id}`} className="hover:text-[#D4AF37] hover:underline">{r.invoice_number || '—'}</Link> : '—'}</td>
                            <td className="px-3 py-2 text-right text-white">{fmtMoney(r.billed_amount)}</td>
                            <td className="px-3 py-2 text-right text-[#D4AF37]">{fmtMoney(r.collected_amount)}</td>
                            <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${STATUS_BADGE(r.status)}`}>{r.status || '—'}</span>{r.excluded_reason && <span data-testid={`tech-detail-excluded-${r.invoice_id}`} className="ml-1 text-[10px] text-red-400 italic">excluded: {r.excluded_reason}</span>}</td>
                            <td className="px-3 py-2 text-[10px] text-[#A3A3A3] uppercase tracking-wider">{(r.credit_source || 'unassigned').replace(/_/g, ' ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {(data.completions_without_invoice || []).length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mb-2">Completed jobs without an invoice</p>
                      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3] uppercase text-[10px] tracking-wider">
                            <tr><th className="px-3 py-2 text-left">Submitted</th><th className="px-3 py-2 text-left">Customer</th><th className="px-3 py-2 text-left">Completion</th><th className="px-3 py-2 text-left">Invoice Decision</th><th className="px-3 py-2 text-left">Note</th></tr>
                          </thead>
                          <tbody>
                            {data.completions_without_invoice.map((c) => (
                              <tr key={c.completion_id} className="border-b border-[#262626]">
                                <td className="px-3 py-2 text-white">{(c.submitted_at || '').slice(0, 10)}</td>
                                <td className="px-3 py-2 text-white">{c.customer_name || '—'}</td>
                                <td className="px-3 py-2 text-[#A3A3A3]">{c.status || '—'}</td>
                                <td className="px-3 py-2 text-[#A3A3A3]">{c.invoice_status || '—'}</td>
                                <td className="px-3 py-2 text-[#A3A3A3]">{c.invoice_status_note || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <AssignTechModal invoice={assignInv} onClose={() => setAssignInv(null)} onAssigned={handleAssigned} />
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// AssignTechModal — iter203: manually credit an unassigned invoice to a tech.
// ────────────────────────────────────────────────────────────
const AssignTechModal = ({ invoice, onClose, onAssigned }) => {
  const [employees, setEmployees] = useState([]);
  const [techId, setTechId] = useState('');
  const [reason, setReason] = useState('');
  const [perfDate, setPerfDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!invoice) return;
    setTechId(''); setReason(''); setPerfDate(invoice.performance_date || '');
    axios.get(`${API}/admin/employees`, { withCredentials: true })
      .then(({ data }) => setEmployees((data || []).filter((e) => e.active !== false)))
      .catch(() => setEmployees([]));
  }, [invoice]);

  const submit = async () => {
    if (!techId) { toast.error('Pick a technician.'); return; }
    if (!reason.trim()) { toast.error('A reason is required.'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/invoices/${invoice.invoice_id}/assign-tech`, {
        technician_id: techId,
        reason: reason.trim(),
        linked_appointment_id: invoice.appointment_id || null,
        linked_completion_id: invoice.completion_id || null,
        performance_date: perfDate || null,
      }, { withCredentials: true });
      toast.success('Invoice credited to technician.');
      onAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign technician.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#141414] border border-[#262626] text-white" data-testid="assign-tech-modal">
        <DialogHeader>
          <DialogTitle className="text-white">Assign Technician</DialogTitle>
          <DialogDescription className="text-[#A3A3A3]">
            {invoice ? <>Credit invoice <b className="text-white">{invoice.invoice_number || invoice.invoice_id}</b> ({fmtMoney(invoice.billed_amount)}) for <b className="text-white">{invoice.customer_name || '—'}</b> to the tech who did the work. The original invoice is preserved — this records a credit overlay with a full audit trail.</> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Technician</label>
            <Select value={techId} onValueChange={setTechId}>
              <SelectTrigger data-testid="assign-tech-select" className="bg-[#0A0A0A] border-[#262626] text-white mt-1"><SelectValue placeholder="Select technician" /></SelectTrigger>
              <SelectContent className="bg-[#141414] border-[#262626] text-white max-h-72">
                {employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Performance / Service Date</label>
            <Input type="date" value={perfDate} onChange={(e) => setPerfDate(e.target.value)} data-testid="assign-tech-date" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
            {invoice?.appointment_id
              ? <p className="text-[10px] text-[#737373] mt-1">Linked to an existing scheduled job — the date will be taken from that job when available.</p>
              : <p className="text-[10px] text-[#737373] mt-1">No linked job — set the date this work was actually performed.</p>}
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">Reason (required)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="assign-tech-reason" placeholder="e.g. Office-entered invoice — work performed by this tech per work order #" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" rows={3} />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="assign-tech-cancel" className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="assign-tech-confirm" className="bg-[#D4AF37] text-black hover:bg-[#c5a233]">{saving ? 'Assigning…' : 'Assign Technician'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────────────────────────────────────
// 2) OPPORTUNITY BOARD
// ────────────────────────────────────────────────────────────
const BUCKET_LABELS = {
  water_heater_over_8yrs:  'Water Heaters > 8 yrs',
  water_heater_over_10yrs: 'Water Heaters > 10 yrs',
  sump_no_battery_backup:  'Sumps w/o Battery Backup',
  membership_prospects:    'Membership Prospects',
  sewer_replacement:       'Sewer Replacement',
  tankless_upgrade:        'Tankless Upgrade',
  repipe:                  'Repipe',
  softener_over_10yrs:     'Softeners > 10 yrs',
};

export const OpportunityBoard = () => {
  const [buckets, setBuckets] = useState({});
  const [active, setActive] = useState('water_heater_over_8yrs');
  const [sortBy, setSortBy] = useState('customer_name');

  useEffect(() => {
    axios.get(`${API}/admin/dashboards/opportunity-board`, { withCredentials: true }).then(({ data }) => setBuckets(data.buckets || {}));
  }, []);

  const rows = buckets[active] || [];
  const sorted = [...rows].sort((a, b) => String(a[sortBy] || '').localeCompare(String(b[sortBy] || '')));

  return (
    <div data-testid="dashboard-opportunity-board" className="space-y-5">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1">Opportunity Board</h2>
        <p className="text-[#A3A3A3] text-sm">Revenue waiting to be picked up. Click a bucket → call list.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {Object.keys(BUCKET_LABELS).map((k) => (
          <button key={k} data-testid={`opp-bucket-${k}`} onClick={() => setActive(k)}
            className={`bg-[#141414] border rounded-md p-3 text-left transition-colors ${active === k ? 'border-[#D4AF37]' : 'border-[#262626] hover:border-[#3f3f3f]'}`}>
            <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider mb-1">{BUCKET_LABELS[k]}</p>
            <p className="text-2xl font-bold text-white">{(buckets[k] || []).length}</p>
          </button>
        ))}
      </div>
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3] uppercase text-[10px] tracking-wider">
            <tr>
              {[['customer_name', 'Customer'], ['city', 'City'], ['zip', 'Zip'], ['technician_name', 'Tech'], ['date_identified', 'Date'], ['install_date', 'Installed']].map(([k, l]) => (
                <th key={k} onClick={() => setSortBy(k)} className={`px-3 py-2 text-left cursor-pointer hover:text-white ${sortBy === k ? 'text-[#D4AF37]' : ''}`} data-testid={`opp-col-${k}`}>{l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-[#A3A3A3]">No customers in this bucket yet.</td></tr>
            ) : sorted.map((r, i) => (
              <tr key={i} className="border-b border-[#262626] hover:bg-[#0A0A0A]" data-testid={`opp-row-${i}`}>
                <td className="px-3 py-2 text-white">{r.customer_name || '—'}</td>
                <td className="px-3 py-2 text-[#A3A3A3]">{r.city || '—'}</td>
                <td className="px-3 py-2 text-[#A3A3A3]">{r.zip || '—'}</td>
                <td className="px-3 py-2 text-[#A3A3A3]">{r.technician_name || '—'}</td>
                <td className="px-3 py-2 text-[#A3A3A3]">{r.date_identified ? new Date(r.date_identified).toLocaleDateString() : '—'}</td>
                <td className="px-3 py-2 text-[#A3A3A3]">{r.install_date || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// 3) REVENUE PIPELINE
// ────────────────────────────────────────────────────────────
export const RevenuePipeline = () => {
  const [data, setData] = useState({});
  useEffect(() => { axios.get(`${API}/admin/dashboards/revenue-pipeline`, { withCredentials: true }).then(({ data }) => setData(data)); }, []);

  const Section = ({ title, total, rows, keys, link, testid }) => (
    <div className="bg-[#141414] border border-[#262626] rounded-md" data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#262626] flex items-center justify-between">
        <p className="text-white font-medium">{title}</p>
        <p className="text-[#D4AF37] font-bold">{total !== undefined ? fmtMoney(total) : `${(rows || []).length} items`}</p>
      </div>
      <div className="divide-y divide-[#262626] max-h-[280px] overflow-y-auto">
        {(rows || []).length === 0 ? (
          <p className="px-4 py-6 text-center text-[#A3A3A3] text-sm">None</p>
        ) : (rows || []).slice(0, 50).map((r, i) => (
          <div key={i} className="px-4 py-2 text-sm flex items-center justify-between gap-3">
            <p className="text-white truncate">{r[keys[0]] || '—'}</p>
            <p className="text-[#A3A3A3] text-xs whitespace-nowrap">{keys[1] ? (r[keys[1]] && typeof r[keys[1]] === 'number' ? fmtMoney(r[keys[1]]) : r[keys[1]]) : ''}</p>
          </div>
        ))}
      </div>
      {link && <Link to={link} className="block px-4 py-2 text-[#D4AF37] text-xs hover:underline border-t border-[#262626]">Open all →</Link>}
    </div>
  );

  return (
    <div data-testid="dashboard-revenue-pipeline" className="space-y-5">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1">Revenue Pipeline</h2>
        <p className="text-[#A3A3A3] text-sm">Money in flight. The smaller these stacks, the healthier the business.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <Section title="Open Estimates" total={data.open_estimates_total} rows={data.open_estimates} keys={['customer_name', 'amount']} link="/admin/estimates" testid="pipeline-estimates" />
        <Section title="Pending Approvals" rows={[{n: data.pending_approvals_count}]} keys={['n']} link="/admin/job-approvals" testid="pipeline-approvals" />
        <Section title="Deferred Payments" total={data.deferred_payments_total} rows={data.deferred_payments} keys={['customer_name', 'amount']} testid="pipeline-deferred" />
        <Section title="HSP Claims Outstanding" rows={data.hsp_outstanding} keys={['customer_name', 'service_type']} testid="pipeline-hsp" />
        <Section title="Unpaid Invoices" total={data.unpaid_invoices_total} rows={data.unpaid_invoices} keys={['customer_name', 'amount']} link="/admin/invoices?status=pending" testid="pipeline-unpaid" />
        <Section title="Membership Renewals (30d)" rows={data.membership_renewals_30d} keys={['customer_name', 'expires_at']} link="/admin/memberships" testid="pipeline-renewals" />
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────
// 4) MARKETING DASHBOARD
// ────────────────────────────────────────────────────────────
const MARKETING_LISTS = {
  water_heater_flush_eligible: 'Water Heater Flush',
  annual_inspection_due:       'Annual Inspection',
  sump_pump_service_due:       'Sump Pump Service',
  members:                     'Members',
  non_members:                 'Non-Members',
};

export const MarketingBoard = () => {
  const [data, setData] = useState({});
  const [activeList, setActiveList] = useState('water_heater_flush_eligible');
  const [selected, setSelected] = useState({});      // {id: true}
  const [composer, setComposer] = useState({ channel: 'email', subject: '', message: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/dashboards/marketing`, { withCredentials: true }).then(({ data }) => setData(data));
  }, []);

  const rows = data[activeList] || [];
  const allSelected = rows.length > 0 && rows.every((r) => selected[r.customer_id]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(rows.map((r) => [r.customer_id, true])));
  };

  const send = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length === 0) { toast.error('Select customers first'); return; }
    if (!composer.message.trim()) { toast.error('Write a message'); return; }
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/admin/dashboards/marketing/bulk-outreach`,
        { customer_ids: ids, channel: composer.channel, subject: composer.subject, message: composer.message },
        { withCredentials: true });
      if (composer.channel === 'call_list') {
        const csv = ['Name,Phone,Address', ...res.call_list.map((c) => `"${c.name || ''}","${c.phone || ''}","${(c.address || '').replace(/"/g, "''")}"`)].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `call-list-${Date.now()}.csv`; a.click();
        URL.revokeObjectURL(url);
        toast.success(`Call list ready: ${res.call_list.length} customers`);
      } else if (res.dry_run) {
        toast.warning(`DRY RUN: ${res.sent} message(s) would have been sent. Real ${composer.channel} delivery is disabled (set MARKETING_OUTREACH_ENABLED=true to enable).`, { duration: 8000 });
      } else {
        toast.success(`Sent to ${res.sent}. ${res.failed.length > 0 ? `${res.failed.length} skipped (missing contact).` : ''}`);
      }
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  return (
    <div data-testid="dashboard-marketing" className="space-y-5">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1">Marketing</h2>
        <p className="text-[#A3A3A3] text-sm">Pick a list, pick customers, send one email/SMS or download a call list.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {Object.entries(MARKETING_LISTS).map(([k, label]) => (
          <button key={k} data-testid={`mkt-list-${k}`} onClick={() => { setActiveList(k); setSelected({}); }}
            className={`bg-[#141414] border rounded-md p-3 text-left transition-colors ${activeList === k ? 'border-[#D4AF37]' : 'border-[#262626] hover:border-[#3f3f3f]'}`}>
            <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider mb-1">{label}</p>
            <p className="text-2xl font-bold text-white">{(data[k] || []).length}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-3">
        <div className="bg-[#141414] border border-[#262626] rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#262626] text-[#A3A3A3] uppercase text-[10px] tracking-wider">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="mkt-select-all" className="accent-[#D4AF37]" /></th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Phone</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-[#A3A3A3]">No customers in this list.</td></tr>
              ) : rows.slice(0, 200).map((r) => (
                <tr key={r.customer_id} data-testid={`mkt-row-${r.customer_id}`} className="border-b border-[#262626] hover:bg-[#0A0A0A]">
                  <td className="px-3 py-2"><input type="checkbox" checked={!!selected[r.customer_id]} onChange={(e) => setSelected({ ...selected, [r.customer_id]: e.target.checked })} className="accent-[#D4AF37]" /></td>
                  <td className="px-3 py-2 text-white">{r.customer_name}</td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{r.email || '—'}</td>
                  <td className="px-3 py-2 text-[#A3A3A3]">{r.phone || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-md p-3 space-y-3 h-fit" data-testid="mkt-composer">
          <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Compose</p>
          <div className="flex gap-1">
            {[
              { k: 'email',     label: 'Email',     icon: Mail        },
              { k: 'sms',       label: 'SMS',       icon: MessageSquare },
              { k: 'call_list', label: 'Call List', icon: Phone       },
            ].map((c) => (
              <button key={c.k} data-testid={`mkt-channel-${c.k}`} onClick={() => setComposer({ ...composer, channel: c.k })}
                className={`flex-1 p-2 rounded border text-xs font-medium flex items-center justify-center gap-1 ${composer.channel === c.k ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]' : 'bg-[#0A0A0A] border-[#262626] text-white'}`}>
                <c.icon className="w-3 h-3" /> {c.label}
              </button>
            ))}
          </div>
          {composer.channel === 'email' && (
            <Input data-testid="mkt-subject" value={composer.subject} onChange={(e) => setComposer({ ...composer, subject: e.target.value })}
              placeholder="Subject" className="bg-[#0A0A0A] border-[#262626] text-white" />
          )}
          <Textarea data-testid="mkt-message" value={composer.message} onChange={(e) => setComposer({ ...composer, message: e.target.value })}
            placeholder={composer.channel === 'call_list' ? 'Notes for the call list (optional)' : 'Message'}
            rows={6} className="bg-[#0A0A0A] border-[#262626] text-white" />
          <Button data-testid="mkt-send" onClick={send} disabled={busy}
            className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
            {composer.channel === 'call_list' ? <><Download className="w-4 h-4 mr-2" /> Download CSV</> : `Send to ${Object.values(selected).filter(Boolean).length} customer(s)`}
          </Button>
        </div>
      </div>
    </div>
  );
};
